import { useState, useEffect, useRef } from "react";
import api from "../../../../api/client";
import { useTranslation } from "react-i18next";
import React from "react";
import { PhoneInput, usePhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { parsePhoneNumberFromString, isValidPhoneNumber } from 'libphonenumber-js';
import CandidateGroupSelector from "../candidateGroups/CandidateGroupSelector";
import { createCandidateGroup, addCandidatesToGroup } from "../candidateGroups/candidateGroupUtils";

interface BulkResumeUploadProps {
    onClose: () => void;
    autoUploadFile?: File | null;
    autoUploadFiles?: File[] | null;
    initialData?: ParsedCandidate[] | null;
    initialUploadId?: string;
    initialTotalFiles?: number;
    candidateGroups?: any[];
    selectedCompanyId?: string;
    companies?: any[];
    onGroupCreated?: (group: any) => void;
}

interface ParsedCandidate {
    source_filename: string;
    original_name: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    countryCode?: string;
    phoneError?: string;
    status: "ready" | "incomplete" | "added" | "failed";
    error?: string;
}

interface Notification {
    id: number;
    message: string;
    severity: "success" | "error";
}

interface JobDescriptionOption {
    id: number;
    title: string;
}

// Custom Notification Component
function CustomNotification({ message, severity, onClose }: { message: string; severity: "success" | "error"; onClose: () => void }) {
    return (
        <div className={`min-w-[300px] max-w-[400px] p-4 rounded-lg shadow-lg border-l-4 ${severity === "success"
            ? "bg-green-50 border-green-500"
            : "bg-red-50 border-red-500"
            } animate-slide-in`}>
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1">
                    <div className={`mt-0.5 ${severity === "success" ? "text-green-600" : "text-red-600"
                        }`}>
                        {severity === "success" ? (
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                        ) : (
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        )}
                    </div>
                    <p className={`text-sm font-medium ${severity === "success" ? "text-green-800" : "text-red-800"
                        }`}>
                        {message}
                    </p>
                </div>
                <button
                    onClick={onClose}
                    className={`flex-shrink-0 rounded hover:bg-opacity-20 p-1 ${severity === "success"
                        ? "text-green-600 hover:bg-green-600"
                        : "text-red-600 hover:bg-red-600"
                        }`}
                >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                </button>
            </div>
        </div>
    );
}

const getMappedErrorKey = (errorMsg: string) => {
    const map: Record<string, string> = {
        "Invalid email format.": "bulkUpload.invalidEmailFormat",
        "This user is already a candidate in your company.": "bulkUpload.candidateAlreadyExists",
        "Candidate with this email already exists.": "bulkUpload.emailAlreadyExists",
        "All fields are required.": "bulkUpload.allFieldsRequired",
        "Aadhaar number must be 12 digits.": "bulkUpload.aadhaarInvalid",
    };
    return map[errorMsg] || errorMsg;
};

/** Best-effort E.164 from resume extraction (often missing country code). */
const resumePhoneToE164 = (raw: any, countryCode?: string) => {
    if (raw == null || raw === '') return '';
    const s = String(raw).trim();
    if (!s) return '';
    
    // If it already starts with +, trust it
    if (s.startsWith('+')) {
        const p = parsePhoneNumberFromString(s);
        if (p?.isValid()) return p.number;
        return s; 
    }

    const digits = s.replace(/\D/g, '');
    if (!digits) return '';

    // If we have an extracted country code (e.g. +1, +44)
    if (countryCode && countryCode.startsWith('+')) {
        const codeNoPlus = countryCode.slice(1);

        // 1. Try prepending the country code (unless digits already start with it)
        if (!digits.startsWith(codeNoPlus)) {
            const full = `${countryCode}${digits}`;
            const p = parsePhoneNumberFromString(full);
            if (p?.isValid()) return p.number;
        }

        // 2. Try parsing +digits (handles cases where digits ALREADY included country code)
        let p = parsePhoneNumberFromString(`+${digits}`);
        if (p?.isValid()) return p.number;

        // 3. Last resort fallback
        return `${countryCode}${digits}`;
    }

    // Fallback logic (defaulting to India if 10 digits)
    let p = parsePhoneNumberFromString(s, 'IN');
    if (p?.isValid()) return p.number;
    
    if (digits.length === 10) return `+91${digits}`;
    
    p = parsePhoneNumberFromString(`+${digits}`);
    if (p?.isValid()) return p.number;
    
    return `+${digits}`;
};

/** A "headless" phone input that manages state but renders a plain HTML input without a flag. */
const HeadlessPhoneInput = ({ value, onChange, className }: { value: string, onChange: (phone: string) => void, className?: string }) => {
    const { inputValue, handlePhoneValueChange, inputRef } = usePhoneInput({
        defaultCountry: 'in',
        value: value,
        onChange: (data) => onChange(data.phone),
    });

    return (
        <input
            ref={inputRef}
            value={inputValue}
            onChange={handlePhoneValueChange}
            type="tel"
            className={className}
        />
    );
};

export default function BulkResumeUpload({
    onClose,
    autoUploadFile,
    autoUploadFiles,
    initialData,
    initialUploadId,
    initialTotalFiles,
    candidateGroups: initialCandidateGroups,
    selectedCompanyId,
    companies,
    onGroupCreated,
}: BulkResumeUploadProps) {
    const { t } = useTranslation();
    const [resumeUpload, setResumeUpload] = useState({
        isUploading: false,
        parsedCandidates: initialData || ([] as ParsedCandidate[]),
    });
    const [extractionStatus, setExtractionStatus] = useState({
        progress: 0,
        total: 0,
        completed: 0,
        taskId: null as string | null
    });
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [isSaving, setIsSaving] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [snackbars, setSnackbars] = useState<Notification[]>([]);
    const [jobDescriptions, setJobDescriptions] = useState<JobDescriptionOption[]>([]);
    const [selectedJobDescriptionId, setSelectedJobDescriptionId] = useState("");
    const [jobDescriptionsLoading, setJobDescriptionsLoading] = useState(false);
    const [candidateGroups, setCandidateGroups] = useState<any[]>(initialCandidateGroups || []);
    const [selectedCandidateGroupId, setSelectedCandidateGroupId] = useState("");
    const [candidateGroupMode, setCandidateGroupMode] = useState<"select" | "create">("select");
    const [newCandidateGroup, setNewCandidateGroup] = useState({
        name: "",
        description: "",
        logo: "🌟",
        company: selectedCompanyId || "",
    });
    const activeTaskId = useRef<string | null>(null);
    const activeUploadId = useRef<string | null>(null);  // tracks current upload_id for cancel
    const wsRef = useRef<WebSocket | null>(null);         // reference to open WebSocket
    const hasAutoUploaded = useRef<any>(null);    // duplication guard (supports File or File[])

    // This will handle our notification pop up.
    const addNotification = (message: string, severity: "success" | "error", autoDismiss: boolean = false) => {
        const id = Date.now() + Math.random();
        const newSnack = { id, message, severity };

        setSnackbars(prev => [...prev, newSnack]);

        if (autoDismiss) {
            setTimeout(() => {
                setSnackbars(prev => prev.filter(x => x.id !== id));
            }, 2000);
        }
    };

    // Auto-connect WS if uploadId is provided initially (Multi-PDF case)
    useEffect(() => {
        if (initialUploadId && initialTotalFiles) {
            activeUploadId.current = initialUploadId;

            // Calculate initial completed count (including skipped files passed in initialData)
            const initialCompleted = initialData ? initialData.length : 0;
            const progress = (initialCompleted / initialTotalFiles) * 100;

            setExtractionStatus(prev => ({
                ...prev,
                total: initialTotalFiles,
                completed: initialCompleted,
                progress: progress
            }));

            setResumeUpload(prev => ({
                ...prev,
                isUploading: true,
                parsedCandidates: initialData || []
            }));

            connectWebSocket(initialUploadId, initialTotalFiles);
        }
    }, [initialUploadId, initialTotalFiles, initialData]);

    // Auto-upload ZIP file if provided
    useEffect(() => {
        if (autoUploadFile && hasAutoUploaded.current !== autoUploadFile) {
            hasAutoUploaded.current = autoUploadFile;
            processZipFile(autoUploadFile);
        }
    }, [autoUploadFile]);

    // Auto-upload multiple individual files if provided
    useEffect(() => {
        if (autoUploadFiles && autoUploadFiles.length > 0 && hasAutoUploaded.current !== autoUploadFiles) {
            hasAutoUploaded.current = autoUploadFiles;
            processMultipleFiles(autoUploadFiles);
        }
    }, [autoUploadFiles]);

    useEffect(() => {
        let mounted = true;

        const loadJobDescriptions = async () => {
            setJobDescriptionsLoading(true);
            try {
                const params = new URLSearchParams({
                    page: "1",
                    page_size: "100",
                });
                const res = await api.get(`job-descriptions/list_with_pagination/?${params.toString()}`);
                const rawResults = Array.isArray(res.data?.results)
                    ? res.data.results
                    : Array.isArray(res.data)
                        ? res.data
                        : [];

                if (!mounted) return;

                setJobDescriptions(
                    rawResults
                        .filter((jd: any) => jd?.id && jd?.title)
                        .map((jd: any) => ({ id: jd.id, title: jd.title }))
                );
            } catch (err) {
                console.error("Failed to load job descriptions for bulk upload", err);
                if (mounted) {
                    addNotification("Failed to load job descriptions.", "error");
                }
            } finally {
                if (mounted) {
                    setJobDescriptionsLoading(false);
                }
            }
        };

        loadJobDescriptions();

        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        if (initialCandidateGroups && initialCandidateGroups.length > 0) {
            setCandidateGroups(initialCandidateGroups);
        }
    }, [initialCandidateGroups]);

    useEffect(() => {
        let mounted = true;
        const loadCandidateGroups = async () => {
            try {
                const params: any = { page_size: 100, ordering: 'name' };
                if (selectedCompanyId && selectedCompanyId !== 'b2c') {
                    params.company = selectedCompanyId;
                }
                const res = await api.get('candidate-groups/', { params });
                const results = Array.isArray(res.data) ? res.data : (res.data.results || []);
                if (mounted && results.length > 0) {
                    setCandidateGroups(results);
                }
            } catch (err) {
                console.warn('Failed to load candidate groups for bulk upload:', err);
            }
        };
        loadCandidateGroups();
        return () => {
            mounted = false;
        };
    }, [selectedCompanyId]);

    const selectedJobDescription = jobDescriptions.find(
        (jd) => String(jd.id) === selectedJobDescriptionId
    );

    const processMultipleFiles = async (files: File[]) => {
        setResumeUpload(prev => ({ ...prev, isUploading: true }));
        setExtractionStatus(prev => ({ ...prev, total: files.length, completed: 0, progress: 0 }));

        const formData = new FormData();
        files.forEach(file => formData.append('resumes', file));

        try {
            const res = await api.post("/admin/parse-resume/", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            const { upload_id, task_id, total_files, skipped = [] } = res.data;

            // Immediately handle skipped files (e.g. wrong type/size)
            if (skipped.length > 0) {
                const failedEntries: ParsedCandidate[] = skipped.map((s: any) => ({
                    source_filename: s.temp_path || `skipped_${Date.now()}_${s.original_name}`,
                    original_name: s.original_name,
                    firstName: "",
                    lastName: "",
                    email: "",
                    phone: "",
                    status: "failed",
                    error: s.error
                }));

                setResumeUpload(prev => ({
                    ...prev,
                    parsedCandidates: [...prev.parsedCandidates, ...failedEntries]
                }));

                setExtractionStatus(prev => {
                    const newCompleted = prev.completed + skipped.length;
                    return { ...prev, completed: newCompleted, progress: (newCompleted / total_files) * 100 };
                });
            }

            if (upload_id) {
                activeUploadId.current = upload_id;
                setExtractionStatus(prev => ({ ...prev, total: total_files, taskId: task_id }));
                connectWebSocket(upload_id, total_files);
            } else if (task_id) {
                activeUploadId.current = null;
                setExtractionStatus(prev => ({ ...prev, total: total_files, taskId: task_id }));
                pollExtractionStatus(task_id);
            }
        } catch (err: any) {
            console.error("Bulk upload failed", err);
            addNotification(`Error uploading resumes: ${err.message || 'Unknown error'}`, 'error', true);
            setResumeUpload(prev => ({ ...prev, isUploading: false }));
        }
    };

    const connectWebSocket = (uploadId: string, totalFiles: number) => {
        // --- Build WS URL (Defensive Programming Pattern) ---
        // 1. Explicit env var override - only use if it looks like a real WS URL
        const envBase = process.env.REACT_APP_WS_URL;
        let wsUrl: string;

        if (envBase && /^wss?:\/\//.test(envBase)) {
            const base = envBase.replace(/\/+$/, '');
            wsUrl = `${base}/ws/bulk-extraction/${uploadId}/`;
        } else {
            // 2. Derive from window.location (The self-healing fallback)
            const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            const host = window.location.host;
            const hostname = window.location.hostname;

            if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
                // Production/staging: Nginx proxies /ws/ to Daphne
                wsUrl = `${protocol}//${host}/ws/bulk-extraction/${uploadId}/`;
            } else {
                // Local dev: connect directly to Django/Daphne on port 8000
                wsUrl = `${protocol}//${hostname}:8000/ws/bulk-extraction/${uploadId}/`;
            }
        }
        // console.log(`[WS] Connecting to: ${wsUrl}`);

        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;  // store reference so we can close it on cancel

        ws.onopen = () => {
            console.log("WebSocket connected for bulk extraction");
        };

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.type === 'extraction_update' && data.result) {
                    const c = data.result;
                    const completed = c.extracted.first_name && c.extracted.last_name && c.extracted.email && c.extracted.mobile;
                    const phoneE164 = resumePhoneToE164(c.extracted.mobile, c.extracted.country_code);
                    const newCandidate: ParsedCandidate = {
                        source_filename: c.temp_file_info.temp_path,
                        original_name: c.temp_file_info.original_name,
                        firstName: c.extracted.first_name || "",
                        lastName: c.extracted.last_name || "",
                        email: c.extracted.email || "",
                        phone: phoneE164,
                        countryCode: c.extracted.country_code || "+91",
                        status: completed ? "ready" : "incomplete",
                    };

                    setResumeUpload(prev => {
                        // De-duplicate by source_filename
                        const isDuplicate = prev.parsedCandidates.some(pc => pc.source_filename === newCandidate.source_filename);
                        if (isDuplicate) return prev;

                        return {
                            ...prev,
                            parsedCandidates: [...prev.parsedCandidates, newCandidate]
                        };
                    });

                    setExtractionStatus(prev => {
                        const newCompleted = prev.completed + 1;
                        const newProgress = (newCompleted / totalFiles) * 100;

                        if (newCompleted >= totalFiles) {
                            // Don't close WS here, let it live until unmount or manual close
                            setResumeUpload(uploadPrev => ({ ...uploadPrev, isUploading: false }));
                            activeTaskId.current = null;
                        }

                        return {
                            ...prev,
                            completed: newCompleted,
                            progress: newProgress
                        };
                    });
                }
            } catch (err) {
                console.error("Error processing websocket message", err);
            }
        };

        ws.onerror = (error) => {
            console.error("WebSocket error", error);
        };

        ws.onclose = () => {
            console.log("WebSocket connection closed for bulk extraction");
            if (wsRef.current === ws) wsRef.current = null;
        };
    };


    // Cancel the in-progress extraction for this user
    const handleCancelExtraction = async () => {
        const uploadId = activeUploadId.current;
        if (!uploadId || isCancelling) return;

        setIsCancelling(true);
        try {
            await api.post(`/admin/cancel-bulk-extraction/${uploadId}/`);
        } catch (err) {
            // Even if the API call fails, we still reset the UI
            console.warn('Cancel API call failed (task may already be done):', err);
        } finally {
            // Always clean up frontend state regardless of API result
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
            activeTaskId.current = null;
            activeUploadId.current = null;
            setResumeUpload(prev => ({ ...prev, isUploading: false }));
            setExtractionStatus({ progress: 0, total: 0, completed: 0, taskId: null });
            setIsCancelling(false);
            addNotification(t('bulkUpload.extractionCancelled', 'Extraction cancelled.'), 'error', true);
        }
    };

    const pollExtractionStatus = async (taskId: string) => {
        // If this isn't the current active task anymore, stop polling
        if (activeTaskId.current !== taskId) return;

        try {
            const res = await api.get(`/admin/bulk-extraction-status/${taskId}/`);
            const data = res.data;

            // Ensure we are still looking at the same task
            if (activeTaskId.current !== taskId) return;

            setExtractionStatus(prev => {
                // Monotonic check: Only update if progress has actually moved forward for this task
                if (prev.taskId === taskId && data.completed_count < prev.completed) {
                    return prev;
                }
                return {
                    progress: data.progress,
                    total: data.total_count,
                    completed: data.completed_count,
                    taskId: taskId
                };
            });

            if (data.status === "completed") {
                const mappedCandidates = data.results.map((c: any) => {
                    const completed = c.extracted.first_name && c.extracted.last_name && c.extracted.email && c.extracted.mobile;
                    const phoneE164 = resumePhoneToE164(c.extracted.mobile, c.extracted.country_code);
                    return {
                        source_filename: c.temp_file_info.temp_path,
                        original_name: c.temp_file_info.original_name,
                        firstName: c.extracted.first_name || "",
                        lastName: c.extracted.last_name || "",
                        email: c.extracted.email || "",
                        phone: phoneE164,
                        countryCode: c.extracted.country_code || "+91",
                        status: completed ? "ready" : "incomplete",
                    };
                });

                setResumeUpload(prev => {
                    const existingFilenames = new Set(prev.parsedCandidates.map(c => c.source_filename));
                    const uniqueNew = mappedCandidates.filter((c: any) => !existingFilenames.has(c.source_filename));
                    return {
                        isUploading: false,
                        parsedCandidates: [...prev.parsedCandidates, ...uniqueNew]
                    };
                });
                activeTaskId.current = null;
                setExtractionStatus(prev => ({ ...prev, taskId: null })); // Stop polling
            } else {
                // Poll again in 1.5 seconds
                setTimeout(() => pollExtractionStatus(taskId), 1500);
            }
        } catch (err) {
            console.error("Polling error:", err);
            // Only handle error if it's the current task
            if (activeTaskId.current === taskId) {
                setResumeUpload(prev => ({ ...prev, isUploading: false }));
                activeTaskId.current = null;
                setExtractionStatus(prev => ({ ...prev, taskId: null }));
                addNotification(t('bulkUpload.pollingFailed', 'Connection lost while parsing. Please check your internet.'), "error");
            }
        }
    };

    // Process ZIP file
    const processZipFile = async (file: File) => {
        const isZipFile = file.type === "application/zip" ||
            file.type === "application/x-zip-compressed" ||
            file.name.toLowerCase().endsWith(".zip");

        if (!isZipFile) {
            addNotification(t('bulkUpload.zipOnly', 'Please upload a ZIP file only'), "error", true);
            return;
        }

        const MAX_FILE_SIZE = Number(process.env.REACT_APP_MAX_FILE_SIZE) || 100 * 1024 * 1024;
        if (file.size > MAX_FILE_SIZE) {
            addNotification(t('bulkUpload.fileSizeExceeded', 'File size exceeds 100 MB limit'), "error", true);
            return;
        }

        setResumeUpload(prev => ({ ...prev, isUploading: true }));
        setExtractionStatus({ progress: 0, total: 0, completed: 0, taskId: null });

        const formData = new FormData();
        formData.append("zip_file", file);

        try {
            const res = await api.post("/admin/upload-bulk-resumes/", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            const { upload_id, task_id, total_files, skipped = [] } = res.data;

            // Immediately handle skipped files (e.g. wrong type/size)
            if (skipped.length > 0) {
                const failedEntries: ParsedCandidate[] = skipped.map((s: any) => ({
                    source_filename: s.temp_path || `skipped_${Date.now()}_${s.original_name}`,
                    original_name: s.original_name,
                    firstName: "",
                    lastName: "",
                    email: "",
                    phone: "",
                    status: "failed",
                    error: s.error
                }));

                setResumeUpload(prev => ({
                    ...prev,
                    parsedCandidates: [...prev.parsedCandidates, ...failedEntries]
                }));

                setExtractionStatus(prev => ({
                    ...prev,
                    completed: prev.completed + skipped.length
                }));
            }

            if (upload_id) {
                const newTaskId = task_id;
                activeTaskId.current = newTaskId;
                activeUploadId.current = upload_id;
                setExtractionStatus(prev => ({ ...prev, total: total_files, taskId: newTaskId }));
                connectWebSocket(upload_id, total_files);
            } else if (task_id) {
                const newTaskId = task_id;
                activeTaskId.current = newTaskId;
                activeUploadId.current = null;
                setExtractionStatus(prev => ({ ...prev, total: total_files, taskId: newTaskId }));
                pollExtractionStatus(newTaskId);
            }
        } catch (err) {
            console.error(err);
            setResumeUpload(prev => ({ ...prev, isUploading: false }));
            addNotification(t('bulkUpload.uploadFailed', 'Failed to upload ZIP file. Please try again.'), "error");
        }
    };

    // Upload and parse resumes (manual upload option - kept for fallback)
    const handleBulkResumeUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        await processZipFile(file);
    };

    // Update editable field
    const handleFieldChange = (index: number, field: keyof ParsedCandidate, value: string) => {
        setResumeUpload(prev => {
            const updated = [...prev.parsedCandidates];
            const candidate = { ...updated[index], [field]: value };

            // Recalculate status after editing
            const completed = candidate.firstName && candidate.lastName && candidate.email && candidate.phone;

            // Only update status if it's not already added
            if (candidate.status !== "added") {
                candidate.status = completed ? "ready" : "incomplete";
                candidate.error = undefined; // Clear previous error on edit
            }

            updated[index] = candidate;

            // If it was selected but now incomplete, deselect it
            if (candidate.status === "incomplete" && selected.has(candidate.source_filename)) {
                setSelected(prevSelected => {
                    const newSet = new Set(prevSelected);
                    newSet.delete(candidate.source_filename);
                    return newSet;
                });
            }

            return { ...prev, parsedCandidates: updated };
        });
    };

    const handlePhoneChange = (index: number, phone: string) => {
        setResumeUpload(prev => {
            const updated = [...prev.parsedCandidates];
            const candidate = { ...updated[index], phone };
            
            // Validate the phone number using libphonenumber-js
            const isValid = isValidPhoneNumber(phone);
            candidate.phoneError = isValid ? "" : t('bulkUpload.invalidPhone', 'Invalid phone number');

            // Recalculate status
            const hasBasicFields = candidate.firstName && candidate.lastName && candidate.email && candidate.phone && !candidate.phoneError;
            
            if (candidate.status !== "added") {
                candidate.status = hasBasicFields ? "ready" : "incomplete";
                candidate.error = undefined;
            }

            updated[index] = candidate;

            if (candidate.status === "incomplete" && selected.has(candidate.source_filename)) {
                setSelected(prevSelected => {
                    const newSet = new Set(prevSelected);
                    newSet.delete(candidate.source_filename);
                    return newSet;
                });
            }

            return { ...prev, parsedCandidates: updated };
        });
    };

    // Toggle selection
    const toggleSelect = (filename: string) => {
        setSelected(prev => {
            const newSet = new Set(prev);
            if (newSet.has(filename)) newSet.delete(filename);
            else newSet.add(filename);
            return newSet;
        });
    };

    const handleSave = async () => {
        setIsSaving(true);

        const updatedCandidates = [...resumeUpload.parsedCandidates];
        const selectedIndices = updatedCandidates
            .map((c, i) => selected.has(c.source_filename) && (c.status === "ready" || c.status === "failed") ? i : -1)
            .filter(i => i !== -1);

        if (selectedIndices.length === 0) {
            setIsSaving(false);
            return;
        }

        const candidatesToProcess = selectedIndices.map(index => {
            const candidate = updatedCandidates[index];
            
            // Split E.164 number into country code and mobile number
            let mobile = candidate.phone;
            let countryCode = candidate.countryCode || "+91";
            
            try {
                const parsed = parsePhoneNumberFromString(candidate.phone);
                if (parsed) {
                    countryCode = `+${parsed.countryCallingCode}`;
                    mobile = parsed.nationalNumber;
                }
            } catch (err) {
                console.warn("Failed to parse phone before saving", err);
            }

            return {
                firstName: candidate.firstName,
                lastName: candidate.lastName,
                email: candidate.email,
                mobile: mobile,
                countryCode: countryCode,
                temp_file_path: candidate.source_filename,
                temp_file_original_name: candidate.original_name,
            };
        });

        let targetGroupId = selectedCandidateGroupId;

        // If in create mode and group name provided, create the group first
        if (candidateGroupMode === 'create' && (newCandidateGroup.name || '').trim()) {
            try {
                const groupCompany = newCandidateGroup.company || (selectedCompanyId && selectedCompanyId !== 'b2c' ? selectedCompanyId : null);
                const createdGroup = await createCandidateGroup({
                    name: newCandidateGroup.name,
                    description: newCandidateGroup.description,
                    logo: newCandidateGroup.logo,
                    companyId: groupCompany,
                });
                targetGroupId = String(createdGroup.id);
                setCandidateGroups(prev => [createdGroup, ...prev.filter(g => g.id !== createdGroup.id)]);
                setSelectedCandidateGroupId(targetGroupId);
                setCandidateGroupMode('select');
                if (onGroupCreated) onGroupCreated(createdGroup);
            } catch (createErr: any) {
                const msg = createErr.response?.data?.name?.[0]
                    || createErr.response?.data?.detail
                    || createErr.response?.data?.error
                    || createErr.message
                    || "Failed to create candidate group";
                addNotification(`❌ ${msg}`, "error", false);
                setIsSaving(false);
                return;
            }
        }

        try {
            const payload = {
                candidates: candidatesToProcess,
                ...(selectedJobDescriptionId ? { job_description_id: Number(selectedJobDescriptionId) } : {}),
                ...(targetGroupId ? { candidate_group_id: Number(targetGroupId), group_id: Number(targetGroupId) } : {}),
            };

            const res = await api.post("/admin/bulk-add-candidates/", payload);

            let successCount = 0;
            const failedCandidates: { name: string; reason: string }[] = [];
            const successfulEmails: string[] = [];

            // The backend returns { message, created_count, errors: [{email, error}, ...] }
            if (res.status === 201 || res.status === 207) {
                const backendErrors = res.data.errors || [];

                selectedIndices.forEach((originalIndex, payloadIndex) => {
                    const candidate = updatedCandidates[originalIndex];
                    const errorObj = backendErrors.find((e: any) => e.index === payloadIndex);

                    if (!errorObj) {
                        // Success
                        successCount++;
                        successfulEmails.push(candidate.email);
                        updatedCandidates[originalIndex] = {
                            ...candidate,
                            status: "added" as const,
                            error: undefined
                        };
                        // Deselect after successful add
                        setSelected(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(candidate.source_filename);
                            return newSet;
                        });
                    } else {
                        // Failure
                        let failReason = errorObj.error || "Failed to add candidate";
                        failReason = getMappedErrorKey(failReason);
                        updatedCandidates[originalIndex] = {
                            ...candidate,
                            status: "failed" as const,
                            error: failReason
                        };
                        failedCandidates.push({
                            name: `${candidate.firstName} ${candidate.lastName}`,
                            reason: failReason
                        });
                    }
                });

                // Assign members to target candidate group if specified
                if (targetGroupId && successCount > 0) {
                    try {
                        let candidateIdsToAssign: number[] = [];
                        if (Array.isArray(res.data?.candidate_ids)) {
                            candidateIdsToAssign = res.data.candidate_ids;
                        } else if (Array.isArray(res.data?.candidates)) {
                            candidateIdsToAssign = res.data.candidates.map((c: any) => c.id || c.candidate_id).filter(Boolean);
                        } else if (Array.isArray(res.data?.results)) {
                            candidateIdsToAssign = res.data.results.map((c: any) => c.id || c.candidate_id).filter(Boolean);
                        }

                        if (candidateIdsToAssign.length === 0 && successfulEmails.length > 0) {
                            try {
                                const listRes = await api.get('admin/candidates/?page=1&page_size=100&ordering=-id');
                                const fetched = listRes.data?.results || [];
                                const emailSet = new Set(successfulEmails.map(e => e.toLowerCase()));
                                candidateIdsToAssign = fetched
                                    .filter((c: any) => c.email && emailSet.has(c.email.toLowerCase()))
                                    .map((c: any) => c.id);
                            } catch (queryErr) {
                                console.warn('Could not query candidates by email for group assignment:', queryErr);
                            }
                        }

                        if (candidateIdsToAssign.length > 0) {
                            await addCandidatesToGroup(targetGroupId, candidateIdsToAssign);
                        }
                    } catch (memberErr) {
                        console.warn('Failed to assign bulk candidates to group:', memberErr);
                    }
                }
            }

            // Update UI state
            setResumeUpload(prev => ({ ...prev, parsedCandidates: updatedCandidates }));

            // Show a single consolidated success notification
            if (successCount > 0) {
                const groupObj = candidateGroups.find(g => String(g.id) === String(targetGroupId));
                const successMsg = groupObj
                    ? `✅ ${successCount} candidate(s) added and assigned to group "${groupObj.name}"`
                    : `✅ ${t('bulkUpload.successCount', '{{count}} user(s) created successfully', { count: successCount })}`;
                addNotification(successMsg, "success", true);
            }

            // Show a single consolidated failure summary if any failed
            if (failedCandidates.length > 0) {
                const summary = failedCandidates.length === 1
                    ? t('bulkUpload.failedCandidate', '{{name}}: {{reason}}', { name: failedCandidates[0].name, reason: t(failedCandidates[0].reason) })
                    : t('bulkUpload.failedMultiple', '{{count}} additions failed. Hover over status for reasons.', { count: failedCandidates.length });
                addNotification(`❌ ${summary}`, "error", false);
            }

        } catch (err: any) {
            console.error("Bulk add candidates failed:", err);
            let globalError = err.response?.data?.error || err.message || "Bulk operation failed";
            addNotification(`❌ ${globalError}`, "error", false);
        } finally {
            setTimeout(() => {
                setIsSaving(false);
            }, 1000);
        }
    };
    // Add these helper functions before the return statement
    const handleSelectAll = () => {
        const selectable = resumeUpload.parsedCandidates.filter(c => c.status === "ready" || c.status === "failed");
        const allSelectableSelected = selectable.length > 0 && selectable.every(c => selected.has(c.source_filename));

        if (allSelectableSelected) {
            setSelected(new Set());
        } else {
            setSelected(new Set(selectable.map(c => c.source_filename)));
        }
    };


    const handleClose = async () => {
        // If extraction is running, cancel it before closing
        if (activeUploadId.current) {
            try {
                await api.post(`/admin/cancel-bulk-extraction/${activeUploadId.current}/`);
            } catch (err) {
                console.warn('Cancel on close failed (task may already be done):', err);
            } finally {
                if (wsRef.current) {
                    wsRef.current.close();
                    wsRef.current = null;
                }
                activeTaskId.current = null;
                activeUploadId.current = null;
            }
        }
        onClose();
        if (resumeUpload.parsedCandidates.some(c => selected.has(c.source_filename))) {
            window.location.reload();
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 overflow-auto z-[9999]" style={{ backdropFilter: 'blur(8px)' }}>
            {/* Notifications Container */}
            <div className="fixed top-[120px] left-1/2 transform -translate-x-1/2 z-50 flex gap-6 justify-center items-start max-w-[90vw]">
                {/* Success Notifications Column */}
                <div className="flex flex-col gap-3">
                    {snackbars
                        .filter(s => s.severity === "success")
                        .map(snack => (
                            <CustomNotification
                                key={snack.id}
                                message={snack.message}
                                severity={snack.severity}
                                onClose={() => setSnackbars(prev => prev.filter(x => x.id !== snack.id))}
                            />
                        ))}
                </div>

                {/* Error Notifications Column */}
                <div className="flex flex-col gap-3">
                    {snackbars
                        .filter(s => s.severity === "error")
                        .map(snack => (
                            <CustomNotification
                                key={snack.id}
                                message={snack.message}
                                severity={snack.severity}
                                onClose={() => setSnackbars(prev => prev.filter(x => x.id !== snack.id))}
                            />
                        ))}
                </div>
            </div>

            <div className="bg-white rounded-xl w-full max-w-6xl h-[90vh] md:h-[85vh] p-3 md:p-6 mt-12 shadow-xl flex flex-col relative text-[10.5px]" style={{ fontSize: '10.5px' }}>
                <h2 className="text-[10.5px] font-semibold mb-3 text-gray-800" style={{ fontSize: '10.5px' }}>{t('bulkUpload.title')}</h2>

                {/* Upload Section - Only show if no file was auto-uploaded */}
                {!autoUploadFile && !initialData && (
                    <div className="mb-4">
                        {!resumeUpload.isUploading && resumeUpload.parsedCandidates.length === 0 && (
                            <div>
                                <label className="flex items-center gap-2 px-3 py-1.5 bg-blue-500 text-white rounded-md shadow cursor-pointer hover:bg-blue-600 transition w-fit text-[10.5px]" style={{ fontSize: '10.5px' }}>
                                    <input
                                        type="file"
                                        accept=".zip"
                                        onChange={handleBulkResumeUpload}
                                        disabled={resumeUpload.isUploading}
                                        className="hidden"
                                    />
                                    {t('bulkUpload.uploadFile')}
                                </label>
                                <p className="text-[10.5px] text-gray-600 mt-1.5" style={{ fontSize: '10.5px' }}>
                                    {t('bulkUpload.zipInfo')}
                                </p>
                            </div>
                        )}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-[10.5px]" style={{ fontSize: '10.5px' }}>
                    {/* Optional Job Description Tag */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex flex-col justify-between text-[10.5px]" style={{ fontSize: '10.5px' }}>
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <label className="block font-medium text-gray-700 text-[10.5px]" style={{ fontSize: '10.5px' }}>
                                    Optional Job Description Tag
                                </label>
                                <span className="text-[10.5px] uppercase tracking-wider font-semibold text-gray-500 bg-gray-200/70 px-1.5 py-0.5 rounded" style={{ fontSize: '10.5px' }}>
                                    {t('common.optional', 'Optional')}
                                </span>
                            </div>
                            <select
                                value={selectedJobDescriptionId}
                                onChange={(e) => setSelectedJobDescriptionId(e.target.value)}
                                disabled={jobDescriptionsLoading || isSaving}
                                className="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-[10.5px] text-gray-900 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none disabled:bg-gray-100"
                                style={{ fontSize: '10.5px' }}
                            >
                                <option value="" style={{ fontSize: '10.5px' }}>Do not attach a JD tag</option>
                                {jobDescriptions.map((jd) => (
                                    <option key={jd.id} value={String(jd.id)} style={{ fontSize: '10.5px' }}>
                                        {jd.title}
                                    </option>
                                ))}
                            </select>
                            {selectedJobDescription && (
                                <p className="text-[10.5px] text-blue-700 mt-1.5 font-medium" style={{ fontSize: '10.5px' }}>
                                    Selected: {selectedJobDescription.title}
                                </p>
                            )}
                        </div>
                        <p className="text-[10.5px] text-gray-500 mt-1.5" style={{ fontSize: '10.5px' }}>
                            If selected, every successfully added candidate in this bulk upload will be tagged to the chosen job description.
                        </p>
                    </div>

                    {/* Optional Candidate Group */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-[10.5px]" style={{ fontSize: '10.5px' }}>
                        <CandidateGroupSelector
                            candidateGroups={candidateGroups}
                            selectedGroupId={selectedCandidateGroupId}
                            onSelectGroup={setSelectedCandidateGroupId}
                            mode={candidateGroupMode}
                            onModeChange={setCandidateGroupMode}
                            newGroup={newCandidateGroup}
                            onNewGroupChange={setNewCandidateGroup}
                            onGroupCreated={(createdGroup) => {
                                setCandidateGroups(prev => [createdGroup, ...prev.filter(g => g.id !== createdGroup.id)]);
                                setSelectedCandidateGroupId(String(createdGroup.id));
                                setCandidateGroupMode('select');
                                if (onGroupCreated) onGroupCreated(createdGroup);
                            }}
                            selectedCompanyId={selectedCompanyId}
                            companies={companies}
                            disabled={isSaving}
                            compact={false}
                        />
                    </div>
                </div>

                {/* Progress bar — always visible on top when extraction is in progress */}
                {resumeUpload.isUploading && (
                    <div className="mb-4 bg-blue-50 border border-blue-100 rounded-lg p-3 text-[10.5px]" style={{ fontSize: '10.5px' }}>
                        <div className="flex justify-between mb-1.5">
                            <span className="text-[10.5px] font-medium text-blue-700 animate-pulse" style={{ fontSize: '10.5px' }}>
                                {t('bulkUpload.parsing')}
                            </span>
                            <span className="text-[10.5px] font-medium text-blue-700" style={{ fontSize: '10.5px' }}>
                                {extractionStatus.completed} / {extractionStatus.total || '?'}
                            </span>
                        </div>
                        <div className="w-full bg-blue-100 rounded-full h-2">
                            <div
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${extractionStatus.progress}%` }}
                            />
                        </div>
                        <p className="text-blue-500 text-[10.5px] mt-1.5" style={{ fontSize: '10.5px' }}>
                            {t('bulkUpload.parsingSub')}
                        </p>
                        <p className="text-blue-400 text-[10.5px] mt-0.5 italic" style={{ fontSize: '10.5px' }}>
                            {t('bulkUpload.closeCancels')}
                        </p>
                    </div>
                )}

                {/* Candidate table — populates in real-time as WS messages arrive */}
                {resumeUpload.parsedCandidates.length === 0 ? (
                    <div className="flex-grow flex flex-col justify-center items-center py-12 text-[10.5px]" style={{ fontSize: '10.5px' }}>
                        {resumeUpload.isUploading ? (
                            <div className="flex flex-col items-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                                <p className="text-blue-600 font-medium animate-pulse text-[10.5px]" style={{ fontSize: '10.5px' }}>{t('bulkUpload.parsing', 'Processing resumes...')}</p>
                            </div>
                        ) : (
                            <>
                                <p className="text-gray-400 text-[10.5px] mb-2" style={{ fontSize: '10.5px' }}>{t('bulkUpload.noCandidates')}</p>
                            </>
                        )}
                    </div>
                ) : (
                    <div className="overflow-x-auto overflow-y-auto flex-grow border rounded-lg bg-gray-50 text-[10.5px]" style={{ fontSize: '10.5px' }}>
                        <table className="w-full border border-gray-300 rounded-lg shadow-sm text-[10.5px]" style={{ fontSize: '10.5px' }}>
                            <thead className="bg-gray-200 sticky top-0 z-10 text-[10.5px]" style={{ fontSize: '10.5px' }}>
                                <tr>
                                    <th className="py-2 px-3 text-left text-[10.5px] font-semibold text-gray-900" style={{ fontSize: '10.5px' }}>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={
                                                    resumeUpload.parsedCandidates.length > 0 &&
                                                    resumeUpload.parsedCandidates.some(c => c.status === "ready" || c.status === "failed") &&
                                                    resumeUpload.parsedCandidates.filter(c => c.status === "ready" || c.status === "failed").every(c => selected.has(c.source_filename))
                                                }
                                                onChange={handleSelectAll}
                                                className="h-3.5 w-3.5 text-blue-600 border-gray-500 rounded cursor-pointer"
                                            />
                                            <span style={{ fontSize: '10.5px' }}>{t('bulkUpload.selectAll')}</span>
                                        </div>
                                    </th>
                                    <th className="py-2 px-3 text-left text-[10.5px] font-semibold text-gray-900" style={{ fontSize: '10.5px' }}>{t('bulkUpload.firstName')}</th>
                                    <th className="py-2 px-3 text-left text-[10.5px] font-semibold text-gray-900" style={{ fontSize: '10.5px' }}>{t('bulkUpload.lastName')}</th>
                                    <th className="py-2 px-3 text-left text-[10.5px] font-semibold text-gray-900" style={{ fontSize: '10.5px' }}>{t('bulkUpload.email')}</th>
                                    <th className="py-2 px-3 text-left text-[10.5px] font-semibold text-gray-900" style={{ fontSize: '10.5px' }}>{t('bulkUpload.phone')}</th>
                                    <th className="py-2 px-3 text-left text-[10.5px] font-semibold text-gray-900 w-[160px] min-w-[160px]" style={{ fontSize: '10.5px' }}>{t('bulkUpload.status')}</th>
                                </tr>
                            </thead>

                            <tbody>
                                {resumeUpload.parsedCandidates.map((c, i) => (
                                    <tr
                                        key={i}
                                        className={`transition-colors ${i % 2 === 0 ? "bg-white" : "bg-gray-50"
                                            } hover:bg-blue-50 text-[10.5px]`}
                                        style={{ fontSize: '10.5px' }}
                                    >
                                        {/* Checkbox */}
                                        <td className="p-1.5 border-b border-gray-300">
                                            <input
                                                type="checkbox"
                                                checked={selected.has(c.source_filename)}
                                                disabled={c.status === "incomplete" || c.status === "added"}
                                                onChange={() => toggleSelect(c.source_filename)}
                                                className={`h-3.5 w-3.5 text-blue-600 border-gray-500 rounded ${c.status === "incomplete" || c.status === "added" ? "opacity-30 cursor-not-allowed" : "cursor-pointer"}`}
                                            />
                                        </td>

                                        {/* First Name */}
                                        <td className="p-1.5 border-b border-gray-300">
                                            <input
                                                type="text"
                                                value={c.firstName}
                                                onChange={e => handleFieldChange(i, "firstName", e.target.value)}
                                                maxLength={50}
                                                className="border border-gray-500 rounded px-2 py-1 w-full text-black font-normal focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-[10.5px]"
                                                style={{ fontSize: '10.5px' }}
                                            />
                                        </td>

                                        {/* Last Name */}
                                        <td className="p-1.5 border-b border-gray-300">
                                            <input
                                                type="text"
                                                value={c.lastName}
                                                onChange={e => handleFieldChange(i, "lastName", e.target.value)}
                                                maxLength={50}
                                                className="border border-gray-500 rounded px-2 py-1 w-full text-black font-normal focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-[10.5px]"
                                                style={{ fontSize: '10.5px' }}
                                            />
                                        </td>

                                        {/* Email */}
                                        <td className="p-1.5 border-b border-gray-300">
                                            <input
                                                type="email"
                                                value={c.email}
                                                maxLength={100}
                                                onChange={e => handleFieldChange(i, "email", e.target.value)}
                                                className="border border-gray-500 rounded px-2 py-1 w-full text-black font-normal focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-[10.5px]"
                                                style={{ fontSize: '10.5px' }}
                                            />
                                        </td>

                                        {/* Phone */}
                                        <td className="p-1.5 border-b border-gray-300 min-w-[200px]">
                                            <div className="flex flex-col gap-1">
                                                <HeadlessPhoneInput
                                                    value={c.phone}
                                                    onChange={(phone) => handlePhoneChange(i, phone)}
                                                    className="!border !border-gray-500 !rounded !px-2 !py-1 !w-full !text-black !font-normal !focus:ring-2 !focus:ring-blue-500 !focus:border-blue-500 !text-[10.5px]"
                                                />
                                                {c.phoneError && (
                                                    <span className="text-[10px] text-red-500 font-medium leading-none" style={{ fontSize: '10px' }}>
                                                        {c.phoneError}
                                                    </span>
                                                )}
                                            </div>
                                        </td>

                                        <td className="p-1.5 border-b border-gray-300">
                                            {(() => {
                                                let tooltip = "";
                                                if (c.status === "failed") tooltip = t(c.error || '') || t('bulkUpload.statusFailed');
                                                else if (c.status === "incomplete") {
                                                    const missing = [];
                                                    if (!c.firstName) missing.push(t('bulkUpload.firstName'));
                                                    if (!c.lastName) missing.push(t('bulkUpload.lastName'));
                                                    if (!c.email) missing.push(t('bulkUpload.email'));
                                                    if (!c.phone) missing.push(t('bulkUpload.phone'));
                                                    tooltip = t('bulkUpload.statusFieldMissingWithFields', {
                                                        fields: missing.join(t('common.listSeparator', ', '))
                                                    });
                                                }

                                                const hasTooltip = c.status === "failed" || c.status === "incomplete";

                                                return (
                                                    <div className="relative group inline-flex items-center gap-1.5">
                                                        <span
                                                            className={`px-2 py-0.5 rounded-full text-[10.5px] font-medium border shadow-sm ${c.status === "ready"
                                                                ? "bg-blue-100 text-blue-700 border-blue-300"
                                                                : c.status === "added"
                                                                    ? "bg-green-100 text-green-700 border-green-300"
                                                                    : c.status === "failed"
                                                                        ? "bg-red-100 text-red-700 border-red-300 cursor-pointer"
                                                                        : "bg-gray-100 text-gray-700 border-gray-300 cursor-pointer"
                                                                }`}
                                                            style={{ fontSize: '10.5px' }}
                                                        >
                                                            {c.status === "ready" ? t('bulkUpload.statusToAdd')
                                                                : c.status === "incomplete" ? t('bulkUpload.statusFieldMissing')
                                                                    : c.status === "added" ? t('bulkUpload.statusAdded')
                                                                        : t('bulkUpload.statusFailed')}
                                                        </span>

                                                        {hasTooltip && (
                                                            <>
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    className="h-3.5 w-3.5 text-gray-400 cursor-pointer hover:text-gray-600 transition-colors"
                                                                    fill="none"
                                                                    viewBox="0 0 24 24"
                                                                    stroke="currentColor"
                                                                >
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                </svg>

                                                                {/* Custom Tooltip - positioned above, but right-aligned to stay within container boundaries */}
                                                                <div className="hidden group-hover:block absolute z-[100] bottom-full right-0 mb-2 w-48 p-2 bg-white text-gray-800 text-[10.5px] rounded shadow-2xl border border-gray-200 pointer-events-none" style={{ fontSize: '10.5px' }}>
                                                                    <div className="relative z-10" style={{ fontSize: '10.5px' }}>
                                                                        {tooltip}
                                                                        {/* Arrow pointing down, aligned with the icon on the right */}
                                                                        <div className="absolute top-full right-2 -mt-1 w-2 h-2 bg-white border-b border-r border-gray-200 rotate-45" />
                                                                    </div>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="flex justify-between mt-4 gap-2">
                    <button
                        onClick={handleClose}
                        className="px-4 py-1.5 bg-gray-200 rounded-md hover:bg-gray-300 text-gray-800 transition hover:bg-red-400 font-normal hover:font-medium text-[10.5px]"
                        style={{ fontSize: '10.5px' }}
                    >
                        {t('bulkUpload.close')}
                    </button>
                    {resumeUpload.parsedCandidates.length > 0 && (
                        <button
                            onClick={handleSave}
                            disabled={isSaving || selected.size === 0}
                            className="px-4 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition disabled:opacity-50 text-[10.5px] font-medium"
                            style={{ fontSize: '10.5px' }}
                        >
                            {isSaving ? t('bulkUpload.saving') : t('bulkUpload.addCandidates')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
};